import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertCircle,
  Camera,
  CircleCheck,
  CircleDot,
  Loader2,
  Lock,
  Shield,
  ShieldCheck,
  Sparkles,
  Trash2,
  User,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useCambiosSinGuardar } from '../hooks/useCambiosSinGuardar';
import BarraCambiosSinGuardar from '../components/ui/BarraCambiosSinGuardar';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  getMedicoProfile,
  updateMedicoProfile,
  uploadAvatar,
  deleteAvatar,
  changePassword,
  type ProfileUpdateData,
} from '../services/profileService';
import type { MedicoCompleto } from '../types/auth';
import { MAX_30, soloAlfanumericoGuiones, soloDigitos } from '../utils/inputFilters';
import styles from './PerfilPage.module.css';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function validarPoliticaPassword(
  password: string,
  email?: string,
): { valido: boolean; mensaje: string } {
  if (!password || password.length < 8)
    return { valido: false, mensaje: 'La contraseña debe tener mínimo 8 caracteres.' };
  if (password.length > 128)
    return { valido: false, mensaje: 'La contraseña no puede exceder 128 caracteres.' };
  if (!/[A-Z]/.test(password))
    return { valido: false, mensaje: 'Debe incluir al menos una letra mayúscula.' };
  if (!/[a-z]/.test(password))
    return { valido: false, mensaje: 'Debe incluir al menos una letra minúscula.' };
  if (!/\d/.test(password))
    return { valido: false, mensaje: 'Debe incluir al menos un número.' };
  if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password))
    return { valido: false, mensaje: 'Debe incluir al menos un carácter especial.' };
  if (/(.)\1{2,}/.test(password))
    return { valido: false, mensaje: 'No puede tener más de 3 caracteres consecutivos iguales.' };
  if (email && password.toLowerCase().includes(email.split('@')[0].toLowerCase()))
    return { valido: false, mensaje: 'La contraseña no puede contener su nombre de usuario.' };
  return { valido: true, mensaje: 'Contraseña válida.' };
}

type TabId = 'general' | 'security';

export default function PerfilPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<TabId>('general');

  // Profile
  const [profile, setProfile] = useState<MedicoCompleto | null>(null);
  const [docId, setDocId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Form
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [telefono, setTelefono] = useState('');
  const [especialidad, setEspecialidad] = useState('');
  const [subespecialidad, setSubespecialidad] = useState('');
  const [centroMedico, setCentroMedico] = useState('');
  const [cedula, setCedula] = useState('');
  const [colegiadoId, setColegiadoId] = useState('');
  const [seisMedicoId, setSeisMedicoId] = useState('');
  const [seisEspecialidadId, setSeisEspecialidadId] = useState('');
  const [seisInstalacionId, setSeisInstalacionId] = useState('');

  // Aviso estándar de cambios sin guardar — solo el formulario de perfil. El de
  // contraseña queda fuera: es una acción, no un estado editable que se descarte.
  const valorPerfil = useMemo(
    () => ({
      nombre, apellidos, telefono, especialidad, subespecialidad,
      centroMedico, cedula, colegiadoId,
      seisMedicoId, seisEspecialidadId, seisInstalacionId,
    }),
    [nombre, apellidos, telefono, especialidad, subespecialidad, centroMedico,
     cedula, colegiadoId, seisMedicoId, seisEspecialidadId, seisInstalacionId],
  );
  const { hayCambios, marcarGuardado, valorGuardado } = useCambiosSinGuardar(valorPerfil, {
    habilitado: !loading,
  });

  const aplicarPerfil = (v: NonNullable<ReturnType<typeof valorGuardado>>) => {
    setNombre(v.nombre); setApellidos(v.apellidos); setTelefono(v.telefono);
    setEspecialidad(v.especialidad); setSubespecialidad(v.subespecialidad);
    setCentroMedico(v.centroMedico); setCedula(v.cedula);
    setColegiadoId(v.colegiadoId); setSeisMedicoId(v.seisMedicoId);
    setSeisEspecialidadId(v.seisEspecialidadId); setSeisInstalacionId(v.seisInstalacionId);
  };

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load() {
      try {
        const data = await getMedicoProfile(user!.uid, user!.email ?? undefined);
        if (cancelled) return;
        if (data) {
          setProfile(data);
          setDocId(data.id);
          setNombre(data.nombre);
          setApellidos(data.apellidos);
          setTelefono(data.telefono);
          setEspecialidad(data.especialidad);
          setSubespecialidad(data.subespecialidad ?? '');
          setCentroMedico(data.centroMedico);
          setCedula(data.cedula ?? '');
          setColegiadoId(data.colegiadoId ?? '');
          setSeisMedicoId(data.seisMedicoId ?? '');
          setSeisEspecialidadId(data.seisEspecialidadId ?? '');
          setSeisInstalacionId(data.seisInstalacionId ?? '');
        } else {
          const nombres = (user!.displayName || '').split(' ');
          const fallback = {
            id: user!.uid,
            nombre: nombres[0] || '',
            apellidos: nombres.slice(1).join(' ') || '',
            nombreCompleto: user!.displayName || user!.email || '',
            email: user!.email || '',
            telefono: '',
            colegiadoId: '',
            especialidad: '',
            subespecialidad: null,
            centroMedico: '',
            estado: 'offline' as const,
            role: 'medico' as const,
            salasAsignadas: [],
            activo: true,
            metodoAuth: 'email_password' as const,
            emailVerificado: user!.emailVerified,
            requiereCambioPassword: false,
            notificacionesSonido: true,
            fondoVirtual: 'none',
            avatarUrl: user!.photoURL,
            stats: { consultasHoy: 0, consultasTotales: 0, tiempoPromedioMin: 0, calificacionPromedio: 0 },
            creadoEn: null as unknown as import('firebase/firestore').Timestamp,
            actualizadoEn: null as unknown as import('firebase/firestore').Timestamp,
            ultimoAcceso: null as unknown as import('firebase/firestore').Timestamp,
          } as MedicoCompleto;
          setProfile(fallback);
          setDocId(user!.uid);
          setNombre(fallback.nombre);
          setApellidos(fallback.apellidos);
        }
      } catch (err) {
        console.error('Error cargando perfil:', err);
        addToast('error', 'Error al cargar el perfil.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user, addToast]);

  // Línea base tras cargar el perfil: recién ahí las ediciones son "pendientes".
  useEffect(() => {
    if (!loading) marcarGuardado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleSaveProfile = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!user || !profile) return;
    if (!nombre.trim() || !apellidos.trim()) {
      addToast('error', 'Nombre y apellidos son obligatorios.');
      return;
    }

    setSaving(true);
    try {
      const data: ProfileUpdateData = {
        nombre: nombre.trim(),
        apellidos: apellidos.trim(),
        telefono: telefono.trim(),
        especialidad: especialidad.trim(),
        subespecialidad: subespecialidad.trim() || null,
        centroMedico: centroMedico.trim(),
        cedula: cedula.trim(),
        colegiadoId: colegiadoId.trim(),
        seisMedicoId: seisMedicoId.trim() || null,
        seisEspecialidadId: seisEspecialidadId.trim() || null,
        seisInstalacionId: seisInstalacionId.trim() || null,
      };
      await updateMedicoProfile(docId || user.uid, data);
      setProfile((prev) =>
        prev
          ? { ...prev, ...data, nombreCompleto: `${data.nombre} ${data.apellidos}` }
          : prev,
      );
      marcarGuardado();
      addToast('success', 'Perfil actualizado correctamente.');
    } catch {
      addToast('error', 'Error al actualizar el perfil.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingAvatar(true);
    try {
      const url = await uploadAvatar(docId || user.uid, file);
      setProfile((prev) => (prev ? { ...prev, avatarUrl: url } : prev));
      addToast('success', 'Foto de perfil actualizada.');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al subir la imagen.');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user) return;
    setUploadingAvatar(true);
    try {
      await deleteAvatar(docId || user.uid);
      setProfile((prev) => (prev ? { ...prev, avatarUrl: null } : prev));
      addToast('success', 'Foto de perfil eliminada.');
    } catch {
      addToast('error', 'Error al eliminar la foto.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!currentPassword) {
      setPasswordError('Ingrese su contraseña actual.');
      return;
    }
    const validation = validarPoliticaPassword(newPassword, user?.email ?? undefined);
    if (!validation.valido) {
      setPasswordError(validation.mensaje);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      addToast('success', 'Contraseña cambiada exitosamente.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cambiar la contraseña.';
      if (msg.includes('wrong-password') || msg.includes('invalid-credential')) {
        setPasswordError('La contraseña actual es incorrecta.');
      } else {
        setPasswordError(msg);
      }
    } finally {
      setChangingPassword(false);
    }
  };

  // ─── Loading / Empty ───────────────────────────────────────

  if (loading) {
    return (
      <MainLayout pageTitle="Mi perfil">
        <div className={styles.loadingContainer}>
          <Loader2 className={styles.spinner} size={24} />
          <span>Cargando perfil…</span>
        </div>
      </MainLayout>
    );
  }

  if (!profile) {
    return (
      <MainLayout pageTitle="Mi perfil">
        <div className={styles.loadingContainer}>
          No se encontró información del perfil.
        </div>
      </MainLayout>
    );
  }

  const displayName =
    profile.nombreCompleto ||
    [profile.nombre, profile.apellidos].filter(Boolean).join(' ') ||
    profile.email ||
    'Usuario';
  const initials = getInitials(displayName);
  const isMagicLink = profile.metodoAuth === 'magic_link';
  const roleLabel =
    profile.role === 'admin'
      ? 'Administrador'
      : profile.role === 'supervisor'
        ? 'Supervisor'
        : 'Médico';

  return (
    <MainLayout pageTitle="Mi perfil">
      <div className={styles.page}>
        {/* ── Profile header card ── */}
        <section className={styles.headerCard}>
          <div className={styles.headerLeft}>
            <div className={styles.avatarWrap}>
              {profile.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt={`Foto de ${displayName}`}
                  className={styles.avatarImage}
                />
              ) : (
                <span className={styles.avatarInitials}>{initials}</span>
              )}
            </div>
            <div className={styles.headerInfo}>
              <h2 className={styles.headerName}>{displayName}</h2>
              <div className={styles.headerMeta}>
                <span className={`${styles.roleBadge} ${profile.role === 'admin' ? styles.roleBadgeAdmin : ''}`}>
                  {roleLabel}
                </span>
                {profile.especialidad && (
                  <span className={styles.metaItem}>{profile.especialidad}</span>
                )}
                {profile.centroMedico && (
                  <>
                    <span className={styles.metaSep}>·</span>
                    <span className={styles.metaItem}>{profile.centroMedico}</span>
                  </>
                )}
              </div>
              <div className={styles.headerEmail}>{profile.email}</div>
            </div>
          </div>

          <div className={styles.headerActions}>
            <Button
              variant="secondary"
              size="sm"
              loading={uploadingAvatar}
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera size={14} />
              Cambiar foto
            </Button>
            {profile.avatarUrl && (
              <Button
                variant="ghost"
                size="sm"
                disabled={uploadingAvatar}
                onClick={handleDeleteAvatar}
              >
                <Trash2 size={14} />
                Eliminar
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className={styles.hiddenInput}
              onChange={handleAvatarChange}
            />
          </div>
        </section>

        {/* ── Stats row ── */}
        {profile.stats && (
          <section className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Consultas hoy</span>
              <span className={styles.statValue}>{profile.stats.consultasHoy ?? 0}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Consultas totales</span>
              <span className={styles.statValue}>{profile.stats.consultasTotales ?? 0}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Tiempo promedio</span>
              <span className={styles.statValue}>
                {profile.stats.tiempoPromedioMin ?? 0}
                <span className={styles.statUnit}> min</span>
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Calificación</span>
              <span className={styles.statValue}>
                {profile.stats.calificacionPromedio
                  ? profile.stats.calificacionPromedio.toFixed(1)
                  : '—'}
              </span>
            </div>
          </section>
        )}

        {/* ── Tabs ── */}
        <div className={styles.tabsBar} role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'general'}
            className={`${styles.tab} ${activeTab === 'general' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <User size={14} /> Información general
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'security'}
            className={`${styles.tab} ${activeTab === 'security' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <Lock size={14} /> Seguridad
          </button>
        </div>

        {/* ── Tab: General ── */}
        {activeTab === 'general' && (
          <form className={styles.card} onSubmit={handleSaveProfile}>
            <div className={styles.cardHead}>
              <div>
                <h3 className={styles.cardTitle}>Información personal</h3>
                <p className={styles.cardSubtitle}>
                  Actualice sus datos profesionales. Los campos de solo lectura
                  los administra el sistema.
                </p>
              </div>
            </div>

            <div className={styles.cardBody}>
              <div className={styles.formGrid}>
                <Input
                  label="Nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
                <Input
                  label="Apellidos"
                  value={apellidos}
                  onChange={(e) => setApellidos(e.target.value)}
                  required
                />
                <Input
                  label="Teléfono"
                  type="tel"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="+507 6000-0000"
                />
                <Input
                  label="Especialidad"
                  value={especialidad}
                  onChange={(e) => setEspecialidad(e.target.value)}
                  placeholder="Ej. Medicina general"
                />
                <Input
                  label="Subespecialidad"
                  maxLength={MAX_30}
                  value={subespecialidad}
                  onChange={(e) => setSubespecialidad(e.target.value.slice(0, MAX_30))}
                  placeholder="Opcional"
                />
                <Input
                  label="Centro médico"
                  maxLength={MAX_30}
                  value={centroMedico}
                  onChange={(e) => setCentroMedico(e.target.value.slice(0, MAX_30))}
                  placeholder="Ej. CSS · Panamá Metro"
                />
                <Input
                  label="Cédula"
                  placeholder="8-888-8888"
                  maxLength={MAX_30}
                  value={cedula}
                  onChange={(e) => setCedula(soloAlfanumericoGuiones(e.target.value))}
                />
                <Input
                  label="No. de Registro de Idoneidad"
                  placeholder="MED-12345"
                  maxLength={MAX_30}
                  value={colegiadoId}
                  onChange={(e) => setColegiadoId(soloAlfanumericoGuiones(e.target.value))}
                />
                <Input
                  label="Código de médico en HCIS"
                  placeholder="Ej. 407718"
                  inputMode="numeric"
                  maxLength={MAX_30}
                  value={seisMedicoId}
                  onChange={(e) => setSeisMedicoId(soloDigitos(e.target.value))}
                />
                <Input
                  label="Código de especialidad en SEIS"
                  placeholder="Ej. 4573"
                  inputMode="numeric"
                  maxLength={MAX_30}
                  value={seisEspecialidadId}
                  onChange={(e) => setSeisEspecialidadId(soloDigitos(e.target.value))}
                />
                <Input
                  label="Código de instalación de salud en SEIS"
                  placeholder="Ej. 136"
                  inputMode="numeric"
                  maxLength={MAX_30}
                  value={seisInstalacionId}
                  onChange={(e) => setSeisInstalacionId(soloDigitos(e.target.value))}
                />
              </div>

              <div className={styles.readOnlyDivider}>
                <span>Información del sistema</span>
              </div>

              <div className={styles.infoGrid}>
                <div className={styles.infoCell}>
                  <span className={styles.infoLabel}>Correo electrónico</span>
                  <span className={styles.infoValue}>{profile.email}</span>
                </div>
                <div className={styles.infoCell}>
                  <span className={styles.infoLabel}>Rol</span>
                  <span className={styles.infoValue}>{roleLabel}</span>
                </div>
                <div className={styles.infoCell}>
                  <span className={styles.infoLabel}>Método de acceso</span>
                  <span className={styles.infoValue}>
                    {isMagicLink ? 'Código de acceso' : 'Correo y contraseña'}
                  </span>
                </div>
              </div>
            </div>

            {/* El guardar vive en BarraCambiosSinGuardar (solo aparece si hay
                cambios). El form de contraseña conserva su propio botón. */}
          </form>
        )}

        {/* ── Tab: Security ── */}
        {activeTab === 'security' && (
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardHeadWithIcon}>
                <div className={styles.cardHeadIcon}>
                  <Shield size={20} aria-hidden="true" />
                </div>
                <div>
                  <h3 className={styles.cardTitle}>Seguridad de la cuenta</h3>
                  <p className={styles.cardSubtitle}>
                    Mantenga su cuenta segura con una contraseña fuerte y única.
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.cardBody}>
              {isMagicLink ? (
                <div className={styles.notice}>
                  <Sparkles size={16} className={styles.noticeIcon} aria-hidden="true" />
                  <div className={styles.noticeBody}>
                    <strong className={styles.noticeTitle}>
                      Su cuenta usa código de acceso (passwordless)
                    </strong>
                    <p className={styles.noticeText}>
                      No necesita contraseña: cada vez que ingrese al portal
                      recibirá un código de un solo uso en su correo
                      institucional.
                    </p>
                  </div>
                </div>
              ) : (
                <div className={styles.securityLayout}>
                  <form onSubmit={handleChangePassword} className={styles.passwordForm}>
                    {passwordError && (
                      <div className={styles.errorMsg} role="alert">
                        <AlertCircle size={14} aria-hidden="true" />
                        {passwordError}
                      </div>
                    )}

                    <Input
                      label="Contraseña actual"
                      type="password"
                      showPasswordToggle
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <Input
                      label="Nueva contraseña"
                      type="password"
                      showPasswordToggle
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <Input
                      label="Confirmar nueva contraseña"
                      type="password"
                      showPasswordToggle
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />

                    <div className={styles.passwordFormActions}>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={changingPassword}
                      >
                        <ShieldCheck size={14} /> Actualizar contraseña
                      </Button>
                    </div>
                  </form>

                  <div className={styles.passwordSidebar}>
                    <div className={styles.strengthPanel}>
                      <h4 className={styles.strengthTitle}>
                        <Lock size={14} aria-hidden="true" />
                        Requisitos de contraseña
                      </h4>
                      <ul className={styles.rulesList}>
                        {([
                          { met: newPassword.length >= 8, label: 'Mínimo 8 caracteres' },
                          { met: /[A-Z]/.test(newPassword), label: 'Una letra mayúscula' },
                          { met: /[a-z]/.test(newPassword), label: 'Una letra minúscula' },
                          { met: /\d/.test(newPassword), label: 'Un número' },
                          { met: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(newPassword), label: 'Un carácter especial' },
                          { met: !(/(.)\1{2,}/.test(newPassword)) && newPassword.length > 0, label: 'Sin 3+ caracteres repetidos' },
                          { met: newPassword.length > 0 && newPassword === confirmPassword, label: 'Las contraseñas coinciden' },
                        ] as { met: boolean; label: string }[]).map((rule) => (
                          <li
                            key={rule.label}
                            className={`${styles.ruleItem} ${
                              newPassword.length === 0
                                ? ''
                                : rule.met
                                  ? styles.ruleItemMet
                                  : styles.ruleItemUnmet
                            }`}
                          >
                            {newPassword.length === 0 ? (
                              <CircleDot size={14} className={styles.ruleIconIdle} aria-hidden="true" />
                            ) : rule.met ? (
                              <CircleCheck size={14} className={styles.ruleIconMet} aria-hidden="true" />
                            ) : (
                              <AlertCircle size={14} className={styles.ruleIconUnmet} aria-hidden="true" />
                            )}
                            {rule.label}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {newPassword.length > 0 && (() => {
                      let score = 0;
                      if (newPassword.length >= 8) score++;
                      if (newPassword.length >= 12) score++;
                      if (/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword)) score++;
                      if (/\d/.test(newPassword)) score++;
                      if (/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(newPassword)) score++;
                      const level = score <= 1 ? 'weak' : score <= 3 ? 'fair' : 'strong';
                      const labels = { weak: 'Débil', fair: 'Aceptable', strong: 'Fuerte' };

                      return (
                        <div className={styles.strengthMeter}>
                          <div className={styles.strengthBarTrack}>
                            <div
                              className={`${styles.strengthBarFill} ${styles[`strength_${level}`]}`}
                              style={{ width: `${(score / 5) * 100}%` }}
                            />
                          </div>
                          <span className={`${styles.strengthLabel} ${styles[`strength_${level}`]}`}>
                            {labels[level]}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <BarraCambiosSinGuardar
        visible={hayCambios}
        guardando={saving}
        onGuardar={() => handleSaveProfile()}
        onDescartar={() => {
          const previo = valorGuardado();
          if (previo) aplicarPerfil(previo);
        }}
      />
    </MainLayout>
  );
}
